import { useGlobalSettings } from "/global-stores";
import { ProductBlocksInventory } from "_types";
import { ReactProductState } from "product";
import { FC } from "react";
import { StoreApi, UseBoundStore } from "zustand";

export const ProductInventorySlider: FC<{
  settings: ProductBlocksInventory["settings"];
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
}> = ({ settings: { threshold }, useProduct }) => {
  const { selectedVariant, product } = useProduct(({ selectedVariant, product }) => ({
    selectedVariant,
    product,
  }));
  const sellPast = selectedVariant?.inventory_policy === "continue";
  const trackInventory = selectedVariant?.inventory_management === "shopify";
  const quantity = selectedVariant?.inventory_quantity ?? 0;
  const hasInventory = quantity > 0;

  const {
    product_data__preorder_metafield,
    product_data__preorder_date_metafield,
    product_data__preorder_limit_metafield,
  } = useGlobalSettings(
    ({
      product_data__preorder_metafield,
      product_data__preorder_date_metafield,
      product_data__preorder_limit_metafield,
    }) => ({
      product_data__preorder_metafield,
      product_data__preorder_date_metafield,
      product_data__preorder_limit_metafield,
    })
  );

  const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
  const [date_ns, date_key] = product_data__preorder_date_metafield.split(".");
  const [limit_ns, limit_key] = product_data__preorder_limit_metafield.split(".");

  const pre_order =
    selectedVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
    product?.metafields?.[preorder_ns]?.[preorder_key];

  const pre_order_limit = +(selectedVariant?.metafields?.[limit_ns]?.[limit_key] ?? 0);

  const available = selectedVariant?.available;
  const percentage =
    pre_order && pre_order_limit
      ? ((quantity + pre_order_limit) / threshold) * 100
      : selectedVariant?.inventory_management !== "shopify" ||
        selectedVariant?.inventory_policy === "continue"
      ? 100
      : !selectedVariant?.available
      ? 0
      : (quantity / threshold) * 100;

  const color = percentage < 33 ? "error" : percentage >= 66 ? "success" : "warning";

  const description =
    pre_order &&
    !hasInventory &&
    available &&
    (!pre_order_limit || quantity + pre_order_limit > threshold)
      ? "On Preorder"
      : pre_order && !hasInventory && available && pre_order_limit && quantity + pre_order_limit > 0
      ? `${quantity + pre_order_limit} items on Preorder`
      : !trackInventory || sellPast || quantity >= threshold
      ? "Available"
      : !available
      ? "Sold out"
      : selectedVariant?.inventory_quantity === 1
      ? "Last item"
      : `${selectedVariant?.inventory_quantity} items left`;

  return (
    <>
      <div className="flex h-2 w-full max-w-xl overflow-hidden rounded-full bg-gray-300">
        <div className="relative h-full flex-1">
          <div
            className="h-full transition-all duration-500 ease-in-out data-[color=error]:bg-error data-[color=success]:bg-success data-[color=warning]:bg-warning"
            style={{ width: `Max(0%, Min(100%, ${percentage}%))` }}
            data-color={color}
          ></div>
        </div>
      </div>
      <span className="text-right text-xs font-medium">{description}</span>
    </>
  );
};
